import { Platform, useWindowDimensions, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { colors } from '@ui/colors'
import Typography from '@ui/typography'
import ButtonsGrid from '@ui/buttons/ButtonsFixed/ButtonsGrid'
import Button from '@ui/buttons/Button'
import {
  sContainerOuter,
  sContainerHeader,
  REVIEW_MASK_PADDING,
} from '../styles'
import { ReactNode } from 'react'

import ReviewMask, {
  MASK_HEIGHT_PORTRAIT,
  MASK_HEIGHT_LANDSCAPE,
} from './ReviewMask'
import Proposal from './ManualProposalModal'
import { Measurement, ResultValue, Side } from '../types'
import ManualOffset from './ManualOffset'
import InsetSheet from '@ui/components/InsetSheet'
import cn from '@ui/utils/cn'
import ManualSidesSwitch from './ManualSidesSwitch'

const NO_OFFSETS = { r: 0, l: 0 }

interface Props {
  measurement: Measurement
  results?: {
    r: ResultValue
    l: ResultValue
  }
  /** Per-side offset in mm, as currently applied to the measurement. */
  offsets?: {
    r: number
    l: number
  }
  /** Side being adjusted. Controlled so callers can highlight its point. */
  side?: Side
  onSideChange?: (side: Side) => void
  onOffsetChange?: (side: Side, offset: number) => void
  onSave?: () => void
  onCancel?: () => void
  disabled?: boolean
}

function Content({
  measurement,
  results,
  offsets = NO_OFFSETS,
  side = 'r',
  onSideChange,
  onOffsetChange,
  onSave,
  onCancel,
  disabled,
}: Props) {
  const { t } = useTranslation(['measurement'])
  const tNamespace = `measurement:app.manual`
  const tl = `${tNamespace}.${measurement}`

  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  return (
    <View className={sContainerOuter}>
      <View className="flex w-full flex-col items-center justify-start gap-8">
        <View className="flex w-full flex-row items-start justify-between gap-8">
          <View
            className={cn(
              sContainerHeader,
              isLandscape && 'w-max flex-1 items-start px-0'
            )}
          >
            <Typography
              variant={isLandscape ? 'LGB' : 'XLB'}
              className={cn('text-center', isLandscape && 'text-left')}
            >
              {t(`${tl}.header.title`)}
            </Typography>
            <Typography
              variant={Platform.select({ default: 'SM', web: 'MD' })}
              className={cn(
                'text-center text-desc',
                isLandscape && 'text-left'
              )}
            >
              {t(`${tl}.header.desc`)}
            </Typography>
          </View>
          {isLandscape && (
            <ManualSidesSwitch
              className="w-max"
              measurement={measurement}
              side={side}
              onChange={(next) => onSideChange?.(next)}
            />
          )}
        </View>

        {results && (
          <ManualOffset
            measurement={measurement}
            side={side}
            result={results[side]}
            offset={offsets[side]}
            disabled={disabled}
            onSideChange={(next) => onSideChange?.(next)}
            onOffsetChange={(offset) => onOffsetChange?.(side, offset)}
          />
        )}
      </View>
      <View className="flex w-full flex-row gap-8">
        <ButtonsGrid>
          <Button
            variant="filled"
            label={t(`${tNamespace}.actions.override.label`)}
            disabled={disabled}
            onPress={onSave}
            colors={[colors.red[600], colors.red[700]]}
          />
          <Button
            variant="outlined"
            label={t(`${tNamespace}.actions.cancel.label`)}
            disabled={disabled}
            onPress={onCancel}
          />
        </ButtonsGrid>
      </View>
    </View>
  )
}

interface ModalProps extends Props {
  open: boolean
  close?: () => void
  childrenOutside?: ReactNode
}

function Modal({ open, childrenOutside, ...props }: ModalProps) {
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  const maskSpace = isLandscape ? MASK_HEIGHT_LANDSCAPE : MASK_HEIGHT_PORTRAIT
  const topSpace = maskSpace + REVIEW_MASK_PADDING * 2

  if (!open) {
    return null
  }

  return (
    <>
      <ReviewMask />
      <InsetSheet
        fullWidth
        topSpace={topSpace}
        childrenOutside={childrenOutside}
      >
        <Manual.Content {...props} />
      </InsetSheet>
    </>
  )
}

const Manual = {
  Proposal,
  Content,
  Modal,
}

Manual.Proposal = Proposal
Manual.Content = Content
Manual.Modal = Modal

export default Manual
